'use client'

import { useCallback, useRef, useState } from 'react'

import type { Point, Stroke } from '@/lib/grader'

/**
 * Captures pointer-event strokes into viewBox-normalised Stroke[] arrays.
 *
 * Coordinate mapping: on each pointer event, we read the SVG element's current
 * transform matrix via `svgEl.getScreenCTM().inverse()` and apply it to the
 * client coordinates. This correctly maps through any CSS scaling, scrolling,
 * and viewport transforms — so the captured coordinates are in the same SVG
 * user-unit space as the KanjiVG target strokes (which KanjiVG emits in a
 * 109×109 viewport). The inverse CTM approach is the canonical way to map DOM
 * client coords → SVG user units without assuming any fixed ratio.
 *
 * Live rendering: the in-progress stroke is mirrored into `current` state on
 * every move so the caller can render the trailing mark AS the user draws
 * (not only after pointer-up). The `currentStroke` ref stays the authoritative
 * buffer so `endStroke` commits without a stale-closure read.
 *
 * No stroke smoothing animation (§5.0.7): strokes are recorded raw.
 */

export interface DrawCaptureState {
    strokes: Stroke[]
    /** In-progress stroke being drawn — for live trailing-mark rendering. */
    current: Stroke
    isEmpty: boolean
    svgRef: React.RefObject<SVGSVGElement | null>
    /** First pointerType seen this frame ('mouse' / 'touch' / 'pen' / 'unknown'). */
    inputTypeRef: React.RefObject<string>
    beginStroke: (e: React.PointerEvent<SVGSVGElement>) => void
    extendStroke: (e: React.PointerEvent<SVGSVGElement>) => void
    endStroke: () => void
    /** Remove the last committed stroke (incremental undo). No-op when empty. */
    undo: () => void
    clear: () => void
}

// A committed stroke must have travelled at least this far (viewBox user-units;
// the canvas is 109×109) to count. iOS Safari occasionally fires
// pointerdown→pointerup with no — or only sub-pixel — intervening pointermove
// events: a "phantom tap". Without this gate such a tap commits a 1-point,
// invisible stroke (rendering needs ≥2 points) that the user never sees, yet it
// inflates the user stroke count and shifts the grader's per-stroke alignment —
// so a correctly drawn kanji gets rejected. Gating at commit time keeps the
// invariant "committed == visible == graded". 4 units sits comfortably below
// the shortest real stroke (a dot/tick spans ~7+ units) and above pointer
// jitter (~1 unit).
const MIN_STROKE_EXTENT = 4

/** Bounding-box diagonal of a stroke in viewBox user-units. */
function strokeExtent(stroke: Stroke): number {
    if (stroke.length < 2) return 0
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const p of stroke) {
        if (p.x < minX) minX = p.x
        if (p.y < minY) minY = p.y
        if (p.x > maxX) maxX = p.x
        if (p.y > maxY) maxY = p.y
    }
    return Math.hypot(maxX - minX, maxY - minY)
}

export function useDrawCapture(): DrawCaptureState {
    const [strokes, setStrokes] = useState<Stroke[]>([])
    const [current, setCurrent] = useState<Stroke>([])
    const currentStroke = useRef<Stroke | null>(null)
    const svgRef = useRef<SVGSVGElement | null>(null)
    const inputTypeRef = useRef('unknown')

    /** Map a pointer event's client coordinates into SVG user-unit space. */
    const clientToSvg = useCallback(
        (clientX: number, clientY: number): Point | null => {
            const svg = svgRef.current
            if (!svg) return null
            const ctm = svg.getScreenCTM()
            if (!ctm) return null
            const pt = svg.createSVGPoint()
            pt.x = clientX
            pt.y = clientY
            const transformed = pt.matrixTransform(ctm.inverse())
            return { x: transformed.x, y: transformed.y }
        },
        []
    )

    const beginStroke = useCallback(
        (e: React.PointerEvent<SVGSVGElement>) => {
            if (inputTypeRef.current === 'unknown')
                inputTypeRef.current = e.pointerType || 'unknown'
            // Deliberately NOT calling setPointerCapture. On iOS WebKit, the
            // per-stroke setPointerCapture/release churn intermittently puts the
            // element into a state where it stops receiving further pointer
            // events for a short window — the "freeze". Touch input already has
            // implicit capture to the original target on iOS, so moves and up
            // still arrive without it.
            const pt = clientToSvg(e.clientX, e.clientY)
            if (!pt) return
            const started = [pt]
            currentStroke.current = started
            setCurrent(started)
        },
        [clientToSvg]
    )

    const extendStroke = useCallback(
        (e: React.PointerEvent<SVGSVGElement>) => {
            if (!currentStroke.current) return
            const pt = clientToSvg(e.clientX, e.clientY)
            if (!pt) return
            const next = [...currentStroke.current, pt]
            currentStroke.current = next
            setCurrent(next) // live trailing-mark render
        },
        [clientToSvg]
    )

    const endStroke = useCallback(() => {
        const finished = currentStroke.current
        currentStroke.current = null
        setCurrent([])
        // Drop phantom taps: only commit strokes with real extent (see
        // MIN_STROKE_EXTENT note). This is also wired to onPointerCancel, so an
        // iOS-interrupted gesture commits its partial stroke only if meaningful.
        if (!finished || strokeExtent(finished) < MIN_STROKE_EXTENT) return
        setStrokes((prev) => [...prev, finished])
    }, [])

    const undo = useCallback(() => {
        setStrokes((prev) => (prev.length === 0 ? prev : prev.slice(0, -1)))
    }, [])

    const clear = useCallback(() => {
        currentStroke.current = null
        setCurrent([])
        setStrokes([])
    }, [])

    return {
        strokes,
        current,
        isEmpty: strokes.length === 0,
        svgRef,
        inputTypeRef,
        beginStroke,
        extendStroke,
        endStroke,
        undo,
        clear,
    }
}
